import { type BrowserOpenOptions, openInBrowser } from "@src/appV2/NativeAdapters/browser.adapter";
import { useCallback } from "react";

import { environmentConfig } from "../environment";
import { useCbhFlag } from "../FeatureFlags";
import { CbhFeatureFlag } from "../FeatureFlags/CbhFeatureFlag";
import { APP_V2_APP_EVENTS } from "../lib";
import { logError, logEvent } from "../lib/analytics/log";
import { useBackdrop } from "../lib/Backdrop/Backdrop";
import { useZendeskSsoToken } from "./useZendeskSsoToken";
import { ZendeskArticleContext } from "./zendesk-article-error-context";

interface OpenArticleOptions {
  browserOpenOptions?: Partial<BrowserOpenOptions>;
}

interface UseZendeskArticleResult {
  openArticle: (articleLink: string, options?: OpenArticleOptions) => Promise<void>;
  isLoading: boolean;
}

/**
 * Hook to open Zendesk articles with SSO authentication
 * @returns Functions and state for opening Zendesk articles
 */
export function useZendeskArticle(): UseZendeskArticleResult {
  const { showBackdrop, hideBackdrop } = useBackdrop();
  const isZendeskSsoEnabled = useCbhFlag(CbhFeatureFlag.ZENDESK_HC_ARTICLE_SSO, {
    defaultValue: false,
  });
  const { isLoading, mutateAsync } = useZendeskSsoToken();

  async function openArticle(articleLink: string, options?: OpenArticleOptions) {
    const { browserOpenOptions } = options ?? {};
    showBackdrop();
    let successContext: string | undefined;
    // Default error context, will be updated based on the operation path
    let loggingContextOnError: string = ZendeskArticleContext.FAILED_TO_OPEN_BROWSER_WITH_SSO_URL;

    try {
      const defaultBrowserConfig: BrowserOpenOptions = {
        url: articleLink,
        ...browserOpenOptions,
      };

      if (!isZendeskSsoEnabled) {
        loggingContextOnError =
          ZendeskArticleContext.FAILED_TO_OPEN_BROWSER_DIRECTLY_WHEN_SSO_DISABLED;
        await openInBrowser(defaultBrowserConfig);
        successContext = ZendeskArticleContext.OPENED_DIRECTLY_SSO_DISABLED;
        return;
      }

      // SSO is enabled path
      // If mutateAsync throws, the catch block will use FAILED_TO_FETCH_SSO_TOKEN
      loggingContextOnError = ZendeskArticleContext.FAILED_TO_FETCH_SSO_TOKEN;
      const ssoTokenData = await mutateAsync();

      const ssoToken = ssoTokenData?.jwt;

      if (!ssoToken) {
        // Token fetch succeeded but no token returned, open directly
        loggingContextOnError = ZendeskArticleContext.FAILED_TO_OPEN_BROWSER_DIRECTLY_WHEN_NO_JWT;
        await openInBrowser(defaultBrowserConfig);
        successContext = ZendeskArticleContext.OPENED_DIRECTLY_NO_JWT;
        return; // Go to finally block
      }

      // Token exists, proceed with SSO URL
      const params = new URLSearchParams();
      params.append("jwt", ssoToken);
      params.append("return_to", articleLink);
      const ssoUrl = `${environmentConfig.REACT_APP_ZENDESK_SSO_URL}?${params.toString()}`;

      loggingContextOnError = ZendeskArticleContext.FAILED_TO_OPEN_BROWSER_WITH_SSO_URL;
      await openInBrowser({
        url: ssoUrl,
        ...browserOpenOptions,
      });
      successContext = ZendeskArticleContext.OPENED_VIA_SSO;
    } catch (error) {
      logError(APP_V2_APP_EVENTS.ZENDESK_ARTICLE_OPENED_ERROR, {
        error,
        metadata: {
          context: loggingContextOnError, // Use the context specific to the failed operation
          articleLink,
        },
      });
      successContext = undefined; // Ensure no success event is logged if an error occurred
    } finally {
      hideBackdrop();
      if (successContext) {
        logEvent(APP_V2_APP_EVENTS.ZENDESK_ARTICLE_OPENED, {
          context: successContext,
          articleLink,
        });
      }
    }
  }

  return {
    openArticle: useCallback(openArticle, [
      isZendeskSsoEnabled,
      mutateAsync,
      showBackdrop,
      hideBackdrop,
    ]),
    isLoading,
  };
}
